import { clamp01, len, smoothstep, type Vec3 } from '../core/math';
import { Ring } from '../core/ring';
import type { Side } from '../pose/landmarks';
import type { PoseFrame } from '../pose/types';
import type { DefenseEvent, DefenseKind, GuardState } from './events';

/**
 * Head-movement and guard recogniser.
 *
 * The same displacement-in-torso-space idea as the punch FSM, applied to the head.
 * Because `headOffset` is measured against the player's *own calibrated guard
 * position* and expressed in shoulder widths, "a slip" means the same number for
 * a 160 cm player at a laptop and a 195 cm player across a room.
 *
 * Blocks are handled differently from evasions: a block has no distinctive
 * trajectory (the hand is already there), so it is a *state* queried at the
 * moment of impact rather than an event detected from motion. Trying to fire a
 * discrete "block event" produces constant false positives whenever a player
 * simply holds a tight guard.
 */

const TUNING = {
  /** Displacement to open an evasion, sw. */
  enter: 0.2,
  /** Displacement to close it (hysteresis avoids chattering at the boundary). */
  exit: 0.12,
  /** Minimum head speed at onset, sw/s — separates a slip from slow drifting. */
  minSpeed: 0.9,
  /** Above this the evasion counts as fully committed. */
  fullDepth: 0.45,
  maxDurationMs: 1400,
  refractoryMs: 90,
};

let nextId = 1;

interface Sample {
  t: number;
  off: Vec3;
  speed: number;
  /** Fused lean-back depth at this instant, sw. */
  pull: number;
}

export class DefenseRecogniser {
  private active = false;
  private startT = 0;
  private peakDepth = 0;
  private peakT = 0;
  private peakOff: Vec3 = { x: 0, y: 0, z: 0 };
  /** Fused lean-back depth at the peak of the movement, sw. */
  private peakPull = 0;
  private maxSpeed = 0;
  private lastEnd = -1e9;
  private path = new Ring<Sample>(60);
  private history = new Ring<Sample>(90);

  /** Continuous state, read by hit resolution every frame. */
  readonly state: GuardState = {
    left: 1,
    right: 1,
    shell: 1,
    headOffX: 0,
    headOffY: 0,
    headOffZ: 0,
    evading: null,
    evasionDepth: 0,
    lastEvasionStartT: 0,
    lastEvasionPeakT: 0,
    lastEvasionKind: null,
    lastEvasionDepth: 0,
    lastEvasionOffX: 0,
  };

  reset(): void {
    this.active = false;
    this.path.clear();
    this.history.clear();
    this.state.evading = null;
    this.state.evasionDepth = 0;
  }

  update(f: PoseFrame): DefenseEvent | null {
    const off = f.headOffset;
    // Head velocity is measured in torso space, which is ~0 during a waist hinge
    // for the same reason the displacement is: the frame rotates with the body.
    // Without folding in the lean rate, the speed gate silently blocks every
    // lean-back — the movement was detected but never allowed to fire.
    const speed = Math.max(len(f.headVel), Math.abs(f.pullRate));
    const pull = Math.max(0, f.pullDepth);
    const sample: Sample = { t: f.t, off: { ...off }, speed, pull };
    this.history.push(sample);

    // `pullDepth` fuses spine tilt with head-on-neck retraction, so a lean-back
    // is a first-class evasion rather than an afterthought. The previous version
    // used only body-frame head z, which is close to blind to the movement: the
    // torso frame rotates with the lean, so the head barely moves *relative to
    // the torso* even when it has physically travelled 30 cm away from the camera.
    const planar = Math.hypot(off.x, off.y);
    const depth = Math.max(planar, pull);

    this.state.left = f.arms.left.guard;
    this.state.right = f.arms.right.guard;
    // A real shell is hands high AND elbows in. Elbow lateral spread is what
    // separates a tight shell from a lazy "hands near face, elbows flared".
    const elbowSpread = (Math.abs(f.arms.left.elbow.x) + Math.abs(f.arms.right.elbow.x)) * 0.5;
    const tuck = 1 - smoothstep(0.34, 0.62, elbowSpread);
    this.state.shell = clamp01(Math.min(f.arms.left.guard, f.arms.right.guard) * (0.55 + 0.45 * tuck));
    this.state.headOffX = off.x;
    this.state.headOffY = off.y;
    this.state.headOffZ = off.z;

    let emitted: DefenseEvent | null = null;

    if (!this.active) {
      if (depth > TUNING.enter && speed > TUNING.minSpeed && f.t - this.lastEnd > TUNING.refractoryMs) {
        this.active = true;
        this.peakDepth = depth;
        this.peakT = f.t;
        this.peakOff = { ...off };
        this.peakPull = pull;
        this.maxSpeed = speed;
        this.path.clear();
        // Back-date the onset to where the head actually started moving.
        //
        // The trigger fires only once displacement passes 0.2 sw AND speed
        // passes 0.9 sw/s, which is 60-100 ms after the movement began once
        // frame quantisation and filter lag are included. Reporting the trigger
        // time as the player's reaction would inflate the headline metric by
        // that much — i.e. it would be measuring this pipeline rather than the
        // human. The punch FSM already back-dates for the same reason.
        this.startT = this.backdatedOnset(f.t);
        this.state.lastEvasionStartT = this.startT;
        this.state.lastEvasionPeakT = f.t;
        this.state.lastEvasionDepth = depth;
        this.state.lastEvasionOffX = off.x;
      }
    } else {
      this.path.push(sample);
      this.maxSpeed = Math.max(this.maxSpeed, speed);
      if (depth > this.peakDepth) {
        this.peakDepth = depth;
        this.peakT = f.t;
        this.peakOff = { ...off };
        this.peakPull = pull;
        this.state.lastEvasionPeakT = f.t;
        this.state.lastEvasionDepth = depth;
        this.state.lastEvasionOffX = off.x;
      }
      if (depth < TUNING.exit || f.t - this.startT > TUNING.maxDurationMs) {
        emitted = this.emit(f);
        this.active = false;
        this.lastEnd = f.t;
      }
    }

    if (this.active) {
      this.state.evading = classifyLive(off, pull);
      this.state.evasionDepth = clamp01(depth / TUNING.fullDepth);
      this.state.lastEvasionKind = this.state.evading;
    } else {
      this.state.evading = null;
      this.state.evasionDepth = 0;
    }

    return emitted;
  }

  /**
   * Walk back through the history buffer to the last frame where the head was
   * still essentially at rest. That frame is when the movement began.
   */
  private backdatedOnset(now: number): number {
    let best = now;
    for (let i = 1; i < this.history.size; i++) {
      const s = this.history.at(i);
      if (!s) break;
      if (now - s.t > 400) break; // no evasion takes longer than this to start
      const d = Math.max(Math.hypot(s.off.x, s.off.y), s.pull);
      best = s.t;
      // Rest position reached — this is the start of the movement.
      if (d < 0.06 && s.speed < 0.5) break;
    }
    return best;
  }

  private emit(f: PoseFrame): DefenseEvent | null {
    if (this.peakDepth < TUNING.enter) return null;
    const { kind, side, confidence } = classifyPath(this.peakOff, this.peakPull, this.path);
    return {
      id: nextId++,
      kind,
      side,
      tStart: this.startT,
      tPeak: this.peakT,
      tEnd: f.t,
      depth: this.peakDepth,
      speed: this.maxSpeed,
      confidence: clamp01(confidence * (0.5 + 0.5 * smoothstep(0.5, 0.9, f.quality))),
    };
  }
}

/** Cheap per-frame classification for hit resolution — no path analysis. */
function classifyLive(off: Vec3, pull: number): DefenseKind {
  // Pull is tested first and against the fused signal: a lean-back that also
  // drifts slightly sideways is still a pull, not a sloppy slip.
  if (pull > 0.2 && pull > Math.abs(off.x) * 0.9) return 'pull';
  if (off.y < -0.22 && Math.abs(off.x) < 0.25) return 'duck';
  if (off.y < -0.15) return 'roll';
  return 'slip';
}

/**
 * Full classification once the movement completes, using the shape of the path.
 * A slip is a mostly-lateral excursion; a roll dips *and* crosses, tracing a U;
 * a duck drops with little lateral travel; a pull retreats along the forward axis.
 */
function classifyPath(
  peak: Vec3,
  peakPull: number,
  path: Ring<Sample>,
): { kind: DefenseKind; side: Side | 'centre'; confidence: number } {
  const ax = Math.abs(peak.x);
  const ay = Math.max(0, -peak.y);
  // Lean-back magnitude comes from the fused spine-tilt signal, not from
  // body-frame head z, which is nearly blind to a lean at the waist.
  const az = Math.max(0, peakPull);
  const side: Side | 'centre' = ax < 0.12 ? 'centre' : peak.x > 0 ? 'right' : 'left';

  // Lateral travel *across* the movement, which a pure duck does not have.
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  for (const s of path.recent()) {
    minX = Math.min(minX, s.off.x);
    maxX = Math.max(maxX, s.off.x);
    minY = Math.min(minY, s.off.y);
  }
  const lateralSweep = Number.isFinite(minX) ? maxX - minX : 0;
  const dip = Number.isFinite(minY) ? Math.max(0, -minY) : 0;

  const scores: Array<[DefenseKind, number]> = [
    ['roll', Math.min(lateralSweep / 0.4, 1) * Math.min(dip / 0.22, 1) * 1.15],
    ['duck', Math.min(ay / 0.3, 1) * (1 - Math.min(ax / 0.3, 1)) * 1.0],
    ['slip', Math.min(ax / 0.28, 1) * (1 - Math.min(ay / 0.35, 1)) * 1.0],
    ['pull', Math.min(az / 0.28, 1) * (1 - Math.min(ax / 0.35, 1)) * 1.1],
    ['lean', Math.min(ax / 0.35, 1) * Math.min(ay / 0.35, 1) * 0.7],
  ];
  scores.sort((a, b) => b[1] - a[1]);
  const [kind, top] = scores[0];
  const second = scores[1][1];
  const margin = clamp01((top - second) / Math.max(0.12, top));
  return { kind, side, confidence: clamp01(0.4 + 0.6 * margin) };
}

export const DEFENSE_TUNING = TUNING;
